import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, resolveDirective as _resolveDirective, withCtx as _withCtx, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "relative !flex-1 h-full bg-gray-100" }
const _hoisted_2 = {
  key: 0,
  class: "absolute top-[10px] left-[10px] bg-white w-[300px] p-3 rounded"
}
const _hoisted_3 = { class: "shrink-0 w-[300px] h-full bg-white py-3" }
const _hoisted_4 = { class: "relative flex-1" }
const _hoisted_5 = { class: "absolute inset-0 overflow-y-auto -my-1" }
const _hoisted_6 = { class: "overflow-hidden divide-y divide-gray-300 pl-3" }
const _hoisted_7 = { class: "!flex-1 line-clamp-1" }
const _hoisted_8 = {
  key: 1,
  class: "px-3"
}

import { computed, inject, onMounted, PropType, provide, Ref, ref, watch } from 'vue';
import { ElNotification } from 'element-plus';
import { debounce, maxBy, minBy } from 'lodash';
import { useI18n } from 'vue-i18n';
import { FilePondOptions } from 'filepond';
import { fabric } from 'fabric';
import { useQueryClient } from 'vue-query';

import { api } from '@/services';
import {
  inspectionsQueryName,
  useGetInspectionImagesByInspectionQuery,
  useStartInspectionSlicingMutation,
} from '@/entities/inspection/hooks';

import { Inspection } from '@/entities/inspection/types';
import { FabricExposition } from '@/core/components/data-display/Fabric/fabric.shared';
import { InspectionGroup } from '@/entities/inspection-group/types';
import { InspectionImage } from '@/entities/inspection-image/types';

import {
  NkButton,
  NkDivider,
  NkFabric,
  NkFilePond,
  NkGroup,
  NkStack,
} from '@/core/components';

import ImageAssemblerFabricImage
  from '@/teams/visionair/components/ImageAssembler/ImageAssemblerFabricImage.vue';
import ImageAssemblerObjectControls
  from '@/teams/visionair/components/ImageAssembler/ImageAssemblerObjectControls.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ImageAssembler',
  props: {
  inspection: {
    type: Number as PropType<Inspection['id']>,
    default: null,
  },
  inspectionGroup: {
    type: Number as PropType<InspectionGroup['id']>,
    required: true,
  },
},
  setup(__props) {

const props = __props



const { t } = useI18n();

const {
  mutateAsync: startSlicingMutation,
  isLoading: isSlicingLoading,
} = useStartInspectionSlicingMutation();

const fabricExposition = ref<FabricExposition>();

const isSaving = ref(false);
const fileUploadErrorTimer: any = ref(null);

const isFirstFetch = ref(true);
const hasUpdate = ref(false);

const getCanvas = () => fabricExposition.value?.getCanvas();

const getAllRect = () => {
  const canvas = getCanvas();
  if (canvas) {
    const objects = canvas._objects;
    const topObject = minBy(objects, 'top');
    const leftObject = minBy(objects, 'left');
    const rightObject = maxBy(objects, (o) => (o.left ?? 0) + (o.width ?? 0));
    const bottomObject = maxBy(objects, (o) => (o.top ?? 0) + (o.height ?? 0));
    return {
      x: leftObject?.left ?? 0,
      y: topObject?.top ?? 0,
      width: Math.abs((leftObject?.left ?? 0) - ((rightObject?.left ?? 0) + (rightObject?.width ?? 0))),
      height: Math.abs((topObject?.top ?? 0) - ((bottomObject?.top ?? 0) + (bottomObject?.height ?? 0))),
    };
  }
  return {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  };
};

const centerView = () => {
  const globalRect = getAllRect();
  const canvas = getCanvas();
  if (canvas) {
    const scaleX = (canvas.width ?? 0) / globalRect.width;
    const scaleY = (canvas.height ?? 0) / globalRect.height;
    const zoom = Math.min(
      scaleX,
      scaleY,
    );
    const canvasSize = {
      width: (canvas.width ?? 0) / zoom,
      height: (canvas.height ?? 0) / zoom,
    };
    canvas.setZoom(1);
    canvas.absolutePan({
      x: globalRect.x - (scaleY > scaleX ? 0 : ((canvasSize.width ?? 0) - globalRect.width) / 2),
      y: globalRect.y - (scaleX > scaleY ? 0 : ((canvasSize.height ?? 0) - globalRect.height) / 2),
    });
    canvas.setZoom(zoom);
    canvas.fire('pan');
  }
};

const queryClient = useQueryClient();

const canvasImages: Ref<InspectionImage[]> = ref([]);

const inspectionQueryData = computed(() => ({ id: props.inspection }));
const hasInspection = computed(() => !!inspectionQueryData.value.id);

useGetInspectionImagesByInspectionQuery(inspectionQueryData, {
  refetchOnWindowFocus: false,
  enabled: hasInspection,
  initialData: () => [],
  onSuccess: (data) => {
    canvasImages.value = data;

    if (!isFirstFetch.value) {
      hasUpdate.value = true;
    } else {
      isFirstFetch.value = false;
    }
  },
});


const inspectionFileDropzoneRef = ref<typeof NkFilePond>();

const isUploadingImage = ref<boolean>(false);

const isCanvasLoading = computed(() => [isUploadingImage.value].some(Boolean));

const selectedImage = ref<InspectionImage['id'][] | null>(null);

const saveFabric = (group: fabric.Group & { id: number }) => {
  isSaving.value = true;
  api.put(`/inspection_images/${ group.id }`, {
    'width': Math.round((group.width ?? 0) * (group.scaleX ?? 0)) < 100 ? null : Math.round((group.width ?? 0) * (group.scaleX ?? 0)),
    'height': Math.round((group.height ?? 0) * (group.scaleY ?? 0)) < 100 ? null : Math.round((group.height ?? 0) * (group.scaleY ?? 0)),
    'x': Math.round((group.left ?? 0)),
    'y': Math.round((group.top ?? 0)),
    'altX': 0,
    'altY': 0,
    'angle': Math.round((group.angle ?? 0)),
    'zoom': 0,
    'zIndex': 0,
    'ratio': Math.round((group.scaleY ?? 0) * 1000) / 1000,
    'visible': true,
    'isShown': true,
    'selected': true,
    'available': true,
    'ratioFront': 0,
    'isDefault': true,
  }).finally(() => {
    isSaving.value = false;
    queryClient.refetchQueries(['inspection-images-by-inspection', inspectionQueryData]);
  });
};

watch(() => selectedImage.value, (value) => {
  const canvas = getCanvas();

  if (canvas) {
    const object = canvas._objects.find((o: any) => value === o.id);
    if (object) {
      canvas.setActiveObject(object);
    } else {
      canvas.discardActiveObject();
    }
    canvas.renderAll();
  }
});

provide('imageAssembler', {
  selectedImage,
  canvasImages,
});

const save = debounce((e) => {
  saveFabric(e.target);
}, 150);

const startSlicing = () => {
  startSlicingMutation({ id: props.inspection }, {
    onSuccess: () => {
      queryClient.refetchQueries([inspectionsQueryName, { id: props.inspection }]);
    },
  })
    .then(() => {
      ElNotification({
        title: t('inspection.notification.slicing.success.title'),
        message: t('inspection.notification.slicing.success.message'),
        type: 'success',
        duration: 10000,
      });
      hasUpdate.value = false;
    }).catch(() => {
    ElNotification({
      title: t('notification.error'),
      type: 'error',
    });
  });
};

const onCanvasReady = () => {
  const canvas = getCanvas();

  if (canvas) {
    canvas.selection = false;
    canvas.imageSmoothingEnabled = false;
    canvas.on('selection:cleared', () => {
      selectedImage.value = null;
    });

    canvas.on('object:modified', save);

    setTimeout(() => {
      centerView();
    }, 1000);
  }
};

const handleFileUpload = debounce(() => {
  const files = inspectionFileDropzoneRef.value?.filePond.getFiles();
  const formData = new FormData();

  if (files && files.length) {
    files.map(o => o.file).forEach(o => formData.append('files[]', o));

    api.post(`/inspections/${ props.inspection }/upload`, formData)
      .then(() => {
        queryClient.refetchQueries(['inspection-images-by-inspection', inspectionQueryData]);
      })
      .catch((e) => {
        fileUploadErrorTimer.value = setTimeout(() => {
          fileUploadErrorTimer.value = null;
        }, 7000);
      })
      .finally(() => {
          isUploadingImage.value = false;
          inspectionFileDropzoneRef.value?.filePond.removeFiles();
        },
      );
  }
}, 200);

onMounted(() => {
  inspectionFileDropzoneRef.value?.filePond._pond.setOptions({
    server: {
      process: handleFileUpload,
    },
  } as FilePondOptions);
});

const {isFullscreen} = inject<any>('injectedWorkspace');

provide('injectedAssemblerRoot', {
  centerView,
});

return (_ctx: any,_cache: any) => {
  const _component_ElAlert = _resolveComponent("ElAlert")!
  const _component_fullscreen = _resolveComponent("fullscreen")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_fullscreen, {
    modelValue: _unref(isFullscreen),
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(isFullscreen) ? (isFullscreen).value = $event : null))
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(NkGroup), {
        spacing: 0,
        class: "h-full",
        noWrap: ""
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _withDirectives((_openBlock(), _createBlock(_unref(NkFabric), {
              ref_key: "fabricExposition",
              ref: fabricExposition,
              options: {
            uniformScaling: true,
            uniScaleKey: '',
          },
              onReady: onCanvasReady,
              class: "absolute inset-0"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(canvasImages.value, (image, i) => {
                  return (_openBlock(), _createBlock(ImageAssemblerFabricImage, {
                    key: image.uri,
                    index: i + 1,
                    image: image,
                    position: canvasImages.value.length + (i + 1) * -1
                  }, null, 8, ["index", "image", "position"]))
                }), 128))
              ]),
              _: 1
            })), [
              [_directive_loading, _unref(isCanvasLoading)]
            ]),
            _createVNode(_unref(NkGroup), {
              spacing: 4,
              class: "absolute bottom-[60px] right-[60px] bg-white bg-opacity-80 p-2 rounded-full"
            }, {
              default: _withCtx(() => [
                (_ctx.$store.getters['connection/isGranted'](['inspection_can_slice']))
                  ? _withDirectives((_openBlock(), _createBlock(_unref(NkButton), {
                      key: 0,
                      disabled: !hasUpdate.value,
                      title: "Start slicing",
                      type: "success",
                      circle: "",
                      icon: "NkFlare",
                      onClick: startSlicing
                    }, null, 8, ["disabled"])), [
                      [_directive_loading, _unref(isSlicingLoading)]
                    ])
                  : _createCommentVNode("", true),
                _createVNode(_unref(NkButton), {
                  type: "white",
                  circle: "",
                  icon: "NkAspectRatio",
                  onClick: centerView
                }),
                _createVNode(_unref(NkButton), {
                  icon:  _unref(isFullscreen) ? 'NkCloseFullscreen' : 'NkOpenInFull',
                  type: 'white',
                  circle: "",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (isFullscreen.value = !_unref(isFullscreen)))
                }, null, 8, ["icon"])
              ]),
              _: 1
            }),
            (selectedImage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  (_openBlock(), _createBlock(ImageAssemblerObjectControls, {
                    key: selectedImage.value,
                    "get-canvas": getCanvas,
                    onDeleted: _cache[1] || (_cache[1] = () => {
            _unref(queryClient).invalidateQueries(['inspection-images-by-inspection', _unref(inspectionQueryData)]);
            _unref(queryClient).refetchQueries(['inspection-images-by-inspection', _unref(inspectionQueryData)]);
          }
        )
                  }))
                ]))
              : _createCommentVNode("", true)
          ]),
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(NkStack), {
              class: "h-full",
              justify: "between"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(canvasImages.value, (element) => {
                        return (_openBlock(), _createBlock(_unref(NkGroup), {
                          key: element.id,
                          class: _normalizeClass([{
                      '!bg-gray-100': selectedImage.value === element.id,
                    }, "h-[42px] items-center py-1 select-none px-3 cursor-pointer group hover:bg-gray-50"]),
                          noWrap: "",
                          position: "apart",
                          onClick: ($event: any) => (selectedImage.value = element.id),
                          title: element.originalName
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(NkGroup), {
                              class: "h-full !flex-1",
                              noWrap: "",
                              position: "apart"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(NkGroup), {
                                  noWrap: "",
                                  class: "!flex-1"
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("span", _hoisted_7, _toDisplayString(element.originalName), 1)
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["class", "onClick", "title"]))
                      }), 128))
                    ])
                  ])
                ]),
                (_ctx.$store.getters['connection/isGranted'](['inspection_can_upload_image']))
                  ? (_openBlock(), _createBlock(_unref(NkDivider), { key: 0 }))
                  : _createCommentVNode("", true),
                (_ctx.$store.getters['connection/isGranted'](['inspection_can_upload_image']))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      (fileUploadErrorTimer.value)
                        ? (_openBlock(), _createBlock(_component_ElAlert, {
                            key: 0,
                            "show-icon": "",
                            type: "error",
                            title: _ctx.$t('notification.error'),
                            onClose: _cache[2] || (_cache[2] = ($event: any) => (fileUploadErrorTimer.value = null))
                          }, null, 8, ["title"]))
                        : _createCommentVNode("", true),
                      _createVNode(_unref(NkFilePond), {
                        ref_key: "inspectionFileDropzoneRef",
                        ref: inspectionFileDropzoneRef,
                        options: {
                allowImagePreview: false,
                storeAsFile: true,
                name: 'files[]',
                immediate: false,
                allowMultiple: true,
                acceptedFileTypes: ['image/*'],
                maxParallelUploads: 50,
              }
                      }, null, 512)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ])), [
            [_directive_loading, _unref(isCanvasLoading)]
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})